import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { defaultDenominatorForm, type DenominatorForm, type MetricForm } from '../../../CreateMetricModal/formUtils';
import { WinsorizationBoundsSection } from '../../../CreateMetricModal/WinsorizationFields/WinsorizationFields';

interface RatioTermWinsorizationFieldsProps {
  context: UseFormReturn<MetricForm>;
  isNumerator: boolean;
  winsorOpen: boolean;
}

export const RatioTermWinsorizationFields: React.FC<RatioTermWinsorizationFieldsProps> = ({
  context,
  isNumerator,
  winsorOpen,
}) => {
  const { control, getValues, setValue } = context;

  const lowerPercentile = useWatch({
    control,
    name: isNumerator ? 'winsorization.lowerPercentile' : ('denominator.winsorLowerPercentile' as never),
  }) as number | undefined;
  const upperPercentile = useWatch({
    control,
    name: isNumerator ? 'winsorization.upperPercentile' : ('denominator.winsorUpperPercentile' as never),
  }) as number | undefined;

  useEffect(() => {
    if (!winsorOpen) {
      return;
    }

    if (isNumerator) {
      if (lowerPercentile === undefined) {
        setValue('winsorization.lowerPercentile', 0, { shouldDirty: false });
      }
      if (upperPercentile === undefined) {
        setValue('winsorization.upperPercentile', 100, { shouldDirty: false });
      }
      return;
    }

    const denominator = getValues('denominator');
    if (
      denominator?.winsorLowerEnabled &&
      denominator.winsorLowerPercentile !== undefined &&
      denominator?.winsorUpperEnabled &&
      denominator.winsorUpperPercentile !== undefined
    ) {
      return;
    }

    setValue(
      'denominator',
      {
        ...(denominator ?? defaultDenominatorForm),
        winsorLowerEnabled: true,
        winsorLowerPercentile: denominator?.winsorLowerPercentile ?? 0,
        winsorUpperEnabled: true,
        winsorUpperPercentile: denominator?.winsorUpperPercentile ?? 100,
      } as DenominatorForm,
      { shouldDirty: false },
    );
  }, [getValues, isNumerator, lowerPercentile, setValue, upperPercentile, winsorOpen]);

  const lowerControlName = isNumerator
    ? 'winsorization.lowerPercentile'
    : ('denominator.winsorLowerPercentile' as never);
  const upperControlName = isNumerator
    ? 'winsorization.upperPercentile'
    : ('denominator.winsorUpperPercentile' as never);

  if (!winsorOpen) {
    return null;
  }

  return (
    <WinsorizationBoundsSection
      context={context}
      lowerControlName={lowerControlName}
      upperControlName={upperControlName}
      lowerPercentile={lowerPercentile}
      upperPercentile={upperPercentile}
    />
  );
};
